// ============================================================================
// Method ↔ Cost coherence audit - Phase 17 of method-first pipeline
// ----------------------------------------------------------------------------
// Pure deterministic checks that compare a scope item's workMethod against
// its costBreakdown and flag where they disagree. Drives a small warning
// chip in Stage 3 so the builder sees the drift before sending the quote.
//
// NOT an AI pass - these are objective checks the estimator would do
// manually: "if the method says we need a waterproofer, the labour lines
// should include a waterproofer", "if access is 2 weeks of scaffold, the
// equipment should have scaffold priced", etc.
//
// Severity:
//   'blocker'  → quote should not go out until resolved
//   'warning'  → should review; a one-click fix is usually available
//   'info'     → worth knowing; not a problem per se
//
// Each issue has an optional `fix` key suggesting the action ("Recompute
// costs" / "Regenerate WMS" / "Add scaffold to cost breakdown manually").
// ============================================================================

const LABOUR_HOURS_PER_DAY = 8;

// why: P1-8 canonical trade synonym table so "Waterproofer" vs "Waterproofing"
// don't flip the set-comparison into a false positive. Each key is the canonical
// short form; values are the variants that should collapse to it. Keep the
// list terse - these are the high-signal AU construction trades that appear in
// workMethod.trades_required + costBreakdown.labour.
const TRADE_SYNONYMS = {
    waterproofer: ['waterproofing', 'membrane installer'],
    concreter: ['concrete finisher', 'concreting'],
    painter: ['painting'],
    tiler: ['tiling'],
    plasterer: ['plastering', 'gyprock'],
    carpenter: ['carpentry', 'framing'],
    electrician: ['electrical'],
    plumber: ['plumbing'],
    glazier: ['glazing'],
    roofer: ['roofing'],
};

function normalizeTrade(t) {
    const key = String(t || '').toLowerCase().trim();
    if (!key) return key;
    if (_tradeSynonymsKillSwitchOn()) return key;
    for (const [canonical, synonyms] of Object.entries(TRADE_SYNONYMS)) {
        if (key === canonical || synonyms.includes(key)) return canonical;
    }
    return key;
}

function _tradeSynonymsKillSwitchOn() {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return localStorage.getItem('qm_disable_trade_synonyms') === 'true';
        }
    } catch { /* unavailable */ }
    return false;
}

function _accessRegexExtendedKillSwitchOn() {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return localStorage.getItem('qm_disable_access_regex_extended') === 'true';
        }
    } catch { /* unavailable */ }
    return false;
}

function _workersMissingWarningKillSwitchOn() {
    // Renamed 2026-05-12 (DEC-COUNCIL-001 P1a.1): this flag suppresses the
    // WMS "labour-line workers missing" advisory (head-count of crew per
    // scope item) — NOT the upcoming job-queue worker container's switches.
    // (Deprecated alias qm_disable_workers_missing_warning removed v5.24.18 —
    // its own comment marked it for removal after v5.21.x; we're at v5.24.x.)
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return localStorage.getItem('qm_disable_method_workers_missing_warning') === 'true';
        }
    } catch { /* unavailable */ }
    return false;
}

function _projectWmsInvariantsKillSwitchOn() {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return localStorage.getItem('qm_disable_project_wms_invariants') === 'true';
        }
    } catch { /* unavailable */ }
    return false;
}

/**
 * Audit a single scope item. Returns an array of issues. Empty array
 * means method and cost agree.
 */
export function auditScopeItemCoherence(item) {
    const issues = [];
    if (!item || typeof item !== 'object') return issues;
    const wm = item.workMethod;
    const cb = item.costBreakdown;

    // No workMethod at all? Not necessarily wrong - some items pre-date the
    // method-first pipeline. Info only.
    if (!wm) {
        if (cb && (cb.totalCost || 0) > 0) {
            // Priced but no method - legacy item. Info only.
            issues.push({
                code: 'no-work-method',
                severity: 'info',
                message: 'No work method attached. Legacy item - method-first features will be weaker here.',
                fix: 'Re-run analysis to enrich the scope with work methods, or edit manually.',
            });
        }
        return issues;
    }

    const tradesInMethod = _normaliseTrades(wm.trades_required);
    const tradesInLabour = new Set(
        (cb?.labour || [])
            .map(l => normalizeTrade(l?.trade))
            .filter(Boolean)
    );

    // 1. Trade mismatch - method calls for trade X but no labour line for X.
    for (const trade of tradesInMethod) {
        const key = normalizeTrade(trade);
        if (!tradesInLabour.has(key)) {
            issues.push({
                code: 'trade-missing-from-costs',
                severity: 'warning',
                message: `Method needs "${trade}" but costs have no labour line for them.`,
                fix: 'Recompute costs - the method-informed pass will add the trade.',
            });
        }
    }

    // 2. Extra trades - costs include a trade the method doesn't mention.
    // Less critical - sometimes the estimator priced a supervisor or
    // apprentice not called out in method. Info only.
    const methodTradeKeys = new Set(tradesInMethod.map(t => normalizeTrade(t)));
    for (const costTrade of tradesInLabour) {
        if (!methodTradeKeys.has(costTrade)) {
            issues.push({
                code: 'trade-extra-in-costs',
                severity: 'info',
                message: `Costs have labour line for "${costTrade}" but method doesn't list them. OK if intentional (supervisor, apprentice).`,
            });
        }
    }

    // 3. Scaffold access but no scaffold equipment line
    const access = String(wm.access_strategy || '').toLowerCase();
    // why: P1-7 widened the signal set so rope access, MEWP, mast climber,
    // spider lift, swing stage + harness/height-safety kit no longer slip
    // past the validator. Kill switch collapses back to the narrow Phase-17
    // set if the new regex ever false-positives on real projects.
    const accessSignals = _accessRegexExtendedKillSwitchOn()
        ? {
            scaffold: /scaffold/,
            ewp: /ewp|boom|scissor|cherry.?picker|elevated/,
            tower: /tower|mobile.?tower/,
            crane: /crane|hoist/,
        }
        : {
            scaffold: /scaffold|swing.?stage/,
            ewp: /ewp|boom|scissor|cherry.?picker|elevated|mewp|elevating work platform|spider lift/,
            tower: /tower|mobile.?tower|mast climber/,
            crane: /crane|hoist/,
            rope: /abseil|rope.?access|\brope\b|harness|height safety/,
        };
    const equipmentDescriptions = (cb?.equipment || [])
        .map(e => String(e?.description || '').toLowerCase())
        .filter(Boolean);
    for (const [signal, re] of Object.entries(accessSignals)) {
        if (re.test(access)) {
            const present = equipmentDescriptions.some(d => re.test(d));
            if (!present) {
                issues.push({
                    code: 'access-missing-from-equipment',
                    severity: 'warning',
                    message: `Method access strategy mentions "${signal}" but equipment breakdown has no matching line.`,
                    fix: 'Recompute costs, or add the hire line manually.',
                });
            }
        }
    }

    // 4. Programme days vs labour hours - sanity check the estimate.
    // If method says 5 working days and labour hours total 8 (one worker for
    // one day), someone's missed capacity. Allow ±25% for rough estimates.
    const programmeDays = Number.isFinite(wm.programme_days) ? wm.programme_days : 0;
    if (programmeDays > 0) {
        // why: P1-9 — if a labour line is missing a workers count, silently
        // defaulting to 1 was under-counting crew-line hours by up to 3x on
        // multi-person crews. Emit a warning and exclude the offending line
        // from the hour-ratio math so the under/over-costed flag doesn't
        // compound the missing-workers defect. Kill switch restores the old
        // "default to 1" behaviour.
        const workersMissingWarn = !_workersMissingWarningKillSwitchOn();
        const labourArr = cb?.labour || [];
        let totalHours = 0;
        for (let li = 0; li < labourArr.length; li++) {
            const l = labourArr[li];
            const h = Number(l?.hours);
            const workers = Number(l?.workers);
            if (workersMissingWarn && (!Number.isFinite(workers) || workers <= 0)) {
                issues.push({
                    code: 'labour-line-missing-workers',
                    severity: 'warning',
                    message: `Labour line "${l?.description || l?.trade || `#${li + 1}`}" has no workers count — hours may be under-counted`,
                    fix: 'Set the workers count (crew size) on this labour line.',
                    scopeIndex: li,
                });
                continue;
            }
            const w = Number.isFinite(workers) && workers > 0 ? workers : 1;
            if (Number.isFinite(h)) totalHours += (h * w);
        }
        const expectedHours = programmeDays * LABOUR_HOURS_PER_DAY;
        // Only flag extreme mismatches: actual < 50% of expected OR > 200%.
        if (expectedHours > 0 && totalHours > 0) {
            const ratio = totalHours / expectedHours;
            if (ratio < 0.5) {
                issues.push({
                    code: 'labour-under-costed',
                    severity: 'warning',
                    message: `Method programme is ${programmeDays}d (\u2248${expectedHours}h at 8h/day) but labour lines total ${Math.round(totalHours)}h. Possibly under-costed.`,
                    fix: 'Recompute costs with method inputs, or increase labour hours.',
                });
            } else if (ratio > 2.0) {
                issues.push({
                    code: 'labour-over-costed',
                    severity: 'info',
                    message: `Method programme is ${programmeDays}d but labour lines total ${Math.round(totalHours)}h. Possibly over-costed or includes overlapping crew.`,
                });
            }
        } else if (expectedHours > 0 && totalHours === 0 && (cb?.totalCost || 0) > 0) {
            issues.push({
                code: 'no-labour-but-priced',
                severity: 'warning',
                message: `Method programme is ${programmeDays} working days but NO labour lines priced. Labour may be hidden in another category.`,
                fix: 'Review cost breakdown \u2014 labour should appear as its own lines.',
            });
        }
    }

    // 5. Risks to method flagged but contingency at baseline.
    // Low-severity nudge: if method explicitly lists risks, the contingency
    // on the item should probably sit above the default (often 10%).
    const methodRisks = Array.isArray(wm.risks_to_method) ? wm.risks_to_method : [];
    const contPct = Number(cb?.contingencyPct) || 0;
    if (methodRisks.length >= 2 && contPct > 0 && contPct <= 10) {
        issues.push({
            code: 'method-risks-no-contingency-uplift',
            severity: 'info',
            message: `Method lists ${methodRisks.length} risks but contingency is ${contPct}% (baseline). Consider raising contingency to reflect risk exposure.`,
        });
    }

    return issues;
}

/**
 * Project-level audit - rolls up per-item issues plus checks project-level
 * coherence (e.g. chosen approach band vs total cost band alignment).
 * Returns { perItem: Map<id, issues[]>, project: issues[], counts }.
 */
export function auditProjectCoherence({ scopeItems = [], projectStrategy = null, projectWms = null }) {
    const perItem = new Map();
    const counts = { blocker: 0, warning: 0, info: 0, byCode: {} };
    // System-managed statutory/admin rows are client-visible pricing lines,
    // not physical work activities. Keep them in Stage 4/5 and totals, but do
    // not require a matching WMS activity or let them distort repair-method
    // heuristics. The pricing reconciler owns their integrity separately.
    const methodScopeItems = scopeItems.filter((item) => item?.excludeFromMethodCoherence !== true);

    for (const item of methodScopeItems) {
        const issues = auditScopeItemCoherence(item);
        if (issues.length > 0) {
            perItem.set(item?.id || String(item?.code || ''), issues);
            for (const iss of issues) {
                counts[iss.severity] = (counts[iss.severity] || 0) + 1;
                counts.byCode[iss.code] = (counts.byCode[iss.code] || 0) + 1;
            }
        }
    }

    const project = [];

    // why: P2-3 + INV1-INV7 — when the caller passes the project-level WMS,
    // reconcile its activity ledger against the scope ledger. This detects
    // orphaned activities (broken scopeItemRef), cost parity drift, material
    // coverage gaps, and unit/programme mismatches the per-item audit can't
    // see because it only looks at one scope card at a time.
    const projectWmsForAudit = projectWms?.structured?.activities
        ? { ...projectWms.structured, activities: projectWms.structured.activities }
        : projectWms;
    if (projectWmsForAudit && Array.isArray(projectWmsForAudit.activities) && !_projectWmsInvariantsKillSwitchOn()) {
        const wmsIssues = _auditProjectWmsInvariants(methodScopeItems, projectWmsForAudit);
        for (const iss of wmsIssues) {
            project.push(iss);
            counts[iss.severity] = (counts[iss.severity] || 0) + 1;
            counts.byCode[iss.code] = (counts.byCode[iss.code] || 0) + 1;
        }
    }

    // Project-level: chosen approach says "low cost" but total quote sits in
    // the high-cost range for this project type - suggests approach-cost
    // drift. Rough heuristic.
    if (projectStrategy?.chosen?.cost_band === 'low') {
        const totalQuoted = methodScopeItems.reduce((s, it) => {
            const a = parseFloat(String(it?.amount || '0').replace(/,/g, '')) || 0;
            return s + a;
        }, 0);
        if (totalQuoted > 80000) {
            project.push({
                code: 'approach-band-mismatch',
                severity: 'info',
                message: `Chosen approach cost band is "low" but total quote is $${Math.round(totalQuoted).toLocaleString()}. Consider revisiting the Strategy pick or re-pricing.`,
            });
        }
    }

    // Project-level: chosen approach says "Targeted repair" but scope has
    // 5+ items - sounds more like a full replacement. Gentle nudge.
    if (projectStrategy?.chosen?.label && /target/i.test(projectStrategy.chosen.label)) {
        if (methodScopeItems.length >= 6) {
            project.push({
                code: 'targeted-approach-many-items',
                severity: 'info',
                message: `Chosen approach "${projectStrategy.chosen.label}" with ${methodScopeItems.length} scope items. Targeted approaches usually have fewer items \u2014 check whether the Strategy pick still fits.`,
            });
        }
    }

    return { perItem, project, counts };
}

/**
 * Human-readable one-line summary of an issues list. Used for toast
 * messages and the Stage 3 warning chip tooltip.
 */
export function summariseCoherence(issues) {
    if (!Array.isArray(issues) || issues.length === 0) return 'Method and cost are in sync.';
    const byLevel = { blocker: 0, warning: 0, info: 0 };
    for (const i of issues) byLevel[i.severity] = (byLevel[i.severity] || 0) + 1;
    const parts = [];
    if (byLevel.blocker) parts.push(`${byLevel.blocker} blocker${byLevel.blocker === 1 ? '' : 's'}`);
    if (byLevel.warning) parts.push(`${byLevel.warning} warning${byLevel.warning === 1 ? '' : 's'}`);
    if (byLevel.info) parts.push(`${byLevel.info} info`);
    return parts.join(' · ');
}

// ─── Internal ─────────────────────────────────────────────────────────────────

function _normaliseTrades(tradesField) {
    if (!Array.isArray(tradesField)) return [];
    return tradesField.map(t => {
        if (typeof t === 'string') return t.trim();
        if (t && typeof t === 'object') return String(t.trade || '').trim();
        return '';
    }).filter(Boolean);
}

// why: INV1-INV7 invariant audit across the project WMS activity ledger vs
// scope ledger. Pure functions, no I/O. Codes match the plan so the Fix
// Center can map each to its fixer action.
function _auditProjectWmsInvariants(scopeItems, projectWms) {
    const issues = [];
    const activities = Array.isArray(projectWms?.activities) ? projectWms.activities : [];
    const scopeById = new Map();
    const scopeByCode = new Map();
    const scopeByTitle = new Map();
    const scopeByActivityId = new Map();
    for (const s of scopeItems) {
        if (s?.id) scopeById.set(String(s.id), s);
        if (s?.code) scopeByCode.set(String(s.code), s);
        if (s?.title) scopeByTitle.set(_normTitle(s.title), s);
        const oneWayRef = s?.activityRef?.activityId || s?.activity_ref?.activity_id;
        if (oneWayRef) scopeByActivityId.set(String(oneWayRef), s);
        for (const ref of (Array.isArray(s?.activityRefs) ? s.activityRefs : [])) {
            if (ref) scopeByActivityId.set(String(ref), s);
        }
    }

    const scopeHoursById = new Map();
    const scopeDirectById = new Map();
    const scopeMaterialsById = new Map();
    for (const s of scopeItems) {
        if (!s?.id) continue;
        const cb = s.costBreakdown || {};
        const hours = (cb.labour || []).reduce((acc, l) => {
            const h = Number(l?.hours);
            const w = Number.isFinite(l?.workers) && l.workers > 0 ? Number(l.workers) : 1;
            return acc + (Number.isFinite(h) ? h * w : 0);
        }, 0);
        scopeHoursById.set(String(s.id), hours);

        const materials = Number(cb.materials?.reduce?.((a, m) => a + (Number(m?.total) || 0), 0)) || 0;
        const labour = Number(cb.labour?.reduce?.((a, l) => a + (Number(l?.total) || 0), 0)) || 0;
        const equipment = Number(cb.equipment?.reduce?.((a, e) => a + (Number(e?.total) || 0), 0)) || 0;
        scopeDirectById.set(String(s.id), materials + labour + equipment);

        const matNames = new Set((cb.materials || [])
            .map(m => String(m?.description || '').trim().toLowerCase())
            .filter(Boolean));
        scopeMaterialsById.set(String(s.id), matNames);
    }

    // Aggregated programme days by scope id (for INV5).
    const scopeProgrammeById = new Map();
    for (const s of scopeItems) {
        if (!s?.id) continue;
        const pd = Number(s?.workMethod?.programme_days);
        scopeProgrammeById.set(String(s.id), Number.isFinite(pd) ? pd : 0);
    }

    const wmsHoursByScope = new Map();
    const wmsDirectByScope = new Map();
    const wmsProgrammeByScope = new Map();
    let prelimsActivityCount = 0;

    const resolveScopeForActivity = (activity, idx) => {
        if (!activity || typeof activity !== 'object') return null;
        const explicitRef = activity.scopeItemRef || activity.scope_item_ref;
        if (explicitRef) {
            const key = String(explicitRef);
            if (scopeById.has(key)) return { scope: scopeById.get(key), reason: 'scopeItemRef' };
            if (scopeByCode.has(key)) return { scope: scopeByCode.get(key), reason: 'scopeCodeRef' };
        }
        const activityId = activity?.id ? String(activity.id) : '';
        if (activityId && scopeByActivityId.has(activityId)) {
            return { scope: scopeByActivityId.get(activityId), reason: 'scopeBackRef' };
        }
        if (activity?.scope_code && scopeByCode.has(String(activity.scope_code))) {
            return { scope: scopeByCode.get(String(activity.scope_code)), reason: 'scopeCode' };
        }
        const titleKey = _normTitle(activity?.title || '');
        if (titleKey && scopeByTitle.has(titleKey)) {
            return { scope: scopeByTitle.get(titleKey), reason: 'title' };
        }
        if (activities.length === scopeItems.length && scopeItems[idx]?.id) {
            return { scope: scopeItems[idx], reason: 'position' };
        }
        return null;
    };

    for (let idx = 0; idx < activities.length; idx++) {
        const a = activities[idx];
        const resolved = resolveScopeForActivity(a, idx);
        const ref = resolved?.scope?.id ? String(resolved.scope.id) : null;

        // INV4 orphan-free
        if (!ref || !scopeById.has(ref)) {
            issues.push({
                code: 'inv4-activity-orphan',
                severity: 'warning',
                message: `WMS activity "${a?.title || a?.id || '(untitled)'}" has no matching scope item (scopeItemRef missing or unknown).`,
                fix: 'Re-link the activity to its scope item, or regenerate the WMS.',
                activityId: a?.id || null,
            });
        } else if (resolved?.reason === 'position') {
            issues.push({
                code: 'inv4-activity-position-linked',
                severity: 'info',
                message: `WMS activity "${a?.title || a?.id || '(untitled)'}" has no durable scope reference; matched by row position for audit only.`,
                fix: 'Regenerate the WMS or refresh scope from Work Method so stable activity references are stamped.',
                activityId: a?.id || null,
                scopeItemRef: ref,
            });
        }

        const cb = a?.costBreakdown || {};
        const hours = (cb.labour || []).reduce((acc, l) => {
            const h = Number(l?.hours);
            const w = Number.isFinite(l?.workers) && l.workers > 0 ? Number(l.workers) : 1;
            return acc + (Number.isFinite(h) ? h * w : 0);
        }, 0);
        const direct = Number(cb.directCostTotal) || 0;
        const pd = Number(a?.programme_days) || 0;

        if (ref) {
            wmsHoursByScope.set(ref, (wmsHoursByScope.get(ref) || 0) + hours);
            wmsDirectByScope.set(ref, (wmsDirectByScope.get(ref) || 0) + direct);
            wmsProgrammeByScope.set(ref, (wmsProgrammeByScope.get(ref) || 0) + pd);
        }

        // INV7 double-count of prelims — flag suspicious repeated prelim lines.
        if (/prelim|preliminary|site establishment|supervision/i.test(String(a?.title || ''))) {
            prelimsActivityCount++;
        }

        // INV6 unit consistency within a single activity's materials.
        const unitsInActivity = new Set();
        for (const m of (cb.materials || [])) {
            const u = String(m?.unit || '').trim().toLowerCase();
            if (u) unitsInActivity.add(u);
        }
        if (unitsInActivity.has('m2') && unitsInActivity.has('m3')) {
            issues.push({
                code: 'inv6-unit-inconsistency',
                severity: 'warning',
                message: `Activity "${a?.title || a?.id}" mixes m² and m³ on materials — check unit conversions.`,
                activityId: a?.id || null,
            });
        }

        // INV3 material-presence: every activity material should appear in the
        // scope's materials list (by description, case-insensitive).
        if (ref && scopeMaterialsById.has(ref)) {
            const scopeMats = scopeMaterialsById.get(ref);
            for (const m of (cb.materials || [])) {
                const desc = String(m?.description || '').trim().toLowerCase();
                if (!desc) continue;
                if (!scopeMats.has(desc)) {
                    issues.push({
                        code: 'inv3-material-missing-from-scope',
                        severity: 'info',
                        message: `WMS activity uses material "${m.description}" but scope item has no matching costBreakdown.materials entry.`,
                        activityId: a?.id || null,
                        scopeItemRef: ref,
                    });
                }
            }
        }
    }

    // INV1 labour-hours sum mismatch per scope item (±10% warning, ±50% blocker).
    for (const [scopeId, scopeHours] of scopeHoursById.entries()) {
        const wmsHours = wmsHoursByScope.get(scopeId) || 0;
        if (scopeHours === 0 && wmsHours === 0) continue;
        const denom = Math.max(scopeHours, wmsHours, 1);
        const drift = Math.abs(scopeHours - wmsHours) / denom;
        if (drift > 0.5) {
            issues.push({
                code: 'inv1-labour-hours-mismatch',
                severity: 'blocker',
                message: `Scope item ${scopeId}: labour hours differ by ${Math.round(drift * 100)}% between scope (${Math.round(scopeHours)}h) and WMS (${Math.round(wmsHours)}h).`,
                scopeItemRef: scopeId,
            });
        } else if (drift > 0.1) {
            issues.push({
                code: 'inv1-labour-hours-mismatch',
                severity: 'warning',
                message: `Scope item ${scopeId}: labour hours differ by ${Math.round(drift * 100)}% between scope (${Math.round(scopeHours)}h) and WMS (${Math.round(wmsHours)}h).`,
                scopeItemRef: scopeId,
            });
        }
    }

    // INV2 direct-cost parity per scope (±$1 warning).
    for (const [scopeId, scopeDirect] of scopeDirectById.entries()) {
        const wmsDirect = wmsDirectByScope.get(scopeId) || 0;
        if (scopeDirect === 0 && wmsDirect === 0) continue;
        if (Math.abs(scopeDirect - wmsDirect) > 1) {
            issues.push({
                code: 'inv2-direct-cost-parity',
                severity: 'warning',
                message: `Scope item ${scopeId}: direct cost differs scope=$${scopeDirect.toFixed(2)} vs WMS=$${wmsDirect.toFixed(2)}.`,
                scopeItemRef: scopeId,
            });
        }
    }

    // INV5 programme-days consistency per scope.
    for (const [scopeId, scopePd] of scopeProgrammeById.entries()) {
        const wmsPd = wmsProgrammeByScope.get(scopeId) || 0;
        if (scopePd === 0 && wmsPd === 0) continue;
        if (Math.abs(scopePd - wmsPd) > Math.max(1, scopePd * 0.25)) {
            issues.push({
                code: 'inv5-programme-days-mismatch',
                severity: 'info',
                message: `Scope item ${scopeId}: programme days differ scope=${scopePd}d vs WMS=${wmsPd}d.`,
                scopeItemRef: scopeId,
            });
        }
    }

    // INV7 double-count of prelims — if 2+ activities look like prelims, flag.
    if (prelimsActivityCount >= 2) {
        issues.push({
            code: 'inv7-prelims-double-count',
            severity: 'warning',
            message: `${prelimsActivityCount} WMS activities look like preliminaries — check the costing sheet doesn't double-count site establishment / supervision.`,
        });
    }

    return issues;
}

function _normTitle(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}
